import { AccountDomain } from '@/client/accounts/types/account-domain.type';
import { DialogContext } from '@/common/context';
import { useForceVerifyDomain } from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { DialogType } from '@/system-settings/enums';
import { Divider } from '@/ui/styled-ui';
import { CircledCheckIcon } from '@/utils/helpers';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledForceVerifyRow = styled.button`
  display: flex;
  align-items: center;
  gap: var(--semi-xsmall-padding);
  width: 100%;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--gray-medium-dark);
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  line-height: var(--xsmall-line-height);
  text-align: left;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

type ForceVerifyRowProps = {
  domain: AccountDomain;
  accountId: string;
};

export const ForceVerifyRow: React.FC<ForceVerifyRowProps> = ({
  domain,
  accountId,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const { forceVerifyDomain, isLoading } = useForceVerifyDomain(accountId);

  const handleForceVerify = () => {
    setDialogData({
      type: DialogType.WARNING,
      show: true,
      showDialogConfirmation: false,
      header: t('account.domains.forceVerify.modal.title'),
      message: t('account.domains.forceVerify.modal.body', {
        domain: domain.domain,
      }),
      confirmButtonLabel: t('account.domains.forceVerify.modal.confirm'),
      onAccept: async () => {
        try {
          await forceVerifyDomain(domain.id);
          toast?.success(
            t('toast.success'),
            t('account.domains.forceVerify.success'),
          );
        } catch {
          toast?.error(
            t('toast.error'),
            t('account.domains.forceVerify.failed'),
          );
        }
      },
    });
  };

  return (
    <>
      <Divider color="var(--gray-dark)" />
      <StyledForceVerifyRow
        type="button"
        disabled={isLoading}
        onClick={(e) => {
          e.stopPropagation();
          handleForceVerify();
        }}
      >
        <CircledCheckIcon size={18} />
        {t('account.domains.forceVerify.row')}
      </StyledForceVerifyRow>
    </>
  );
};
